import axios from 'axios'
import router from '@/router'
import Vue from 'vue'

// Configuração base do axios com protocolo específico
const getBaseURL = () => {
  // Verifica se existe variável de ambiente específica
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL
  }
  const defaultURL = '/api'
  return defaultURL
}

const baseURL = getBaseURL()

const api = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

// Interceptor para requisições
api.interceptors.request.use(
  (config) => {    
    // Adiciona token de autenticação se existir
    const token = localStorage.getItem('_token')
    if (token && token.startsWith('Bearer ')) {
      config.headers.Authorization = token
    }
    
    // Se for FormData, remove o Content-Type para o axios definir automaticamente
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para respostas
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Tratamento de erros globais
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('token')
      localStorage.removeItem('_token')
      localStorage.removeItem('user-nexa')
      
      // Armazena mensagem para exibir após redirecionamento
      sessionStorage.setItem('auth_expired', 'true')
      
      // Redireciona para login usando router
      if (router) {
        router.push('/login').catch(() => {})
      } else {
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

/**
 * Service class para todas as operações HTTP
 * 
 * ENDPOINTS PÚBLICOS (não requerem autenticação):
 * - GET /public/profile/:username - Busca perfil público de um usuário pelo username
 * 
 * ENDPOINTS AUTENTICADOS (requerem token):
 * - Todas as rotas em /espaco-nexa/*
 */
class ApiService {
  /**
   * Requisição GET
   * @param {string} url - Endpoint da API
   * @param {object} params - Query parameters
   * @param {object} config - Configurações adicionais do axios
   */
  static async get(url, params = {}, config = {}) {
    try {
      const response = await api.get(url, { params, ...config })
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição POST
   * @param {string} url - Endpoint da API
   * @param {object} data - Dados a serem enviados
   * @param {object} config - Configurações adicionais do axios
   */
  static async post(url, data = {}, config = {}) {
    try {
      const response = await api.post(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição PUT
   * @param {string} url - Endpoint da API
   * @param {object} data - Dados a serem enviados
   * @param {object} config - Configurações adicionais do axios
   */
  static async put(url, data = {}, config = {}) {
    try {
      const response = await api.put(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição PATCH
   * @param {string} url - Endpoint da API
   * @param {object} data - Dados a serem enviados
   * @param {object} config - Configurações adicionais do axios
   */
  static async patch(url, data = {}, config = {}) {
    try {
      const response = await api.patch(url, data, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição DELETE
   * @param {string} url - Endpoint da API
   * @param {object} config - Configurações adicionais do axios
   */
  static async delete(url, config = {}) {
    try {
      const response = await api.delete(url, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição HEAD
   * @param {string} url - Endpoint da API
   * @param {object} config - Configurações adicionais do axios
   */
  static async head(url, config = {}) {
    try {
      const response = await api.head(url, config)
      return response.headers
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição OPTIONS
   * @param {string} url - Endpoint da API
   * @param {object} config - Configurações adicionais do axios
   */
  static async options(url, config = {}) {
    try {
      const response = await api.options(url, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Upload de arquivo
   * @param {string} url - Endpoint da API
   * @param {FormData} formData - Dados do formulário com arquivo
   * @param {function} onUploadProgress - Callback para progresso do upload
   */
  static async upload(url, formData, onUploadProgress = null) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
      
      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress
      }
      
      const response = await api.post(url, formData, config)
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Download de arquivo
   * @param {string} url - Endpoint da API
   * @param {string} filename - Nome do arquivo para download
   */
  static async download(url, filename = 'download') {
    try {
      const response = await api.get(url, {
        responseType: 'blob'
      })
      
      // Cria link temporário para download
      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
      
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  }

  /**
   * Requisição com retry automático
   * @param {function} requestFn - Função de requisição
   * @param {number} maxRetries - Número máximo de tentativas
   * @param {number} delay - Delay entre tentativas (ms)
   */
  static async withRetry(requestFn, maxRetries = 3, delay = 1000) {
    let lastError
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await requestFn()
      } catch (error) {
        lastError = error
        
        if (attempt === maxRetries) {
          break
        }
        
        await new Promise(resolve => setTimeout(resolve, delay))
        delay *= 2 // Exponential backoff
      }
    }
    
    throw lastError
  }

  /**
   * Tratamento centralizado de erros
   * @param {object} error - Erro do axios
   */
  static handleError(error) {
    if (error.response) {
      // Erro de resposta do servidor (4xx, 5xx)
      const { status, data } = error.response
      
      // Prioriza a mensagem da API, depois mensagem genérica, depois status
      let message = `Erro ${status}`
      if (data?.message) {
        message = data.message
      } else if (data?.error) {
        message = data.error
      } else if (data && typeof data === 'string') {
        message = data
      }
      
      return {
        type: 'response_error',
        status,
        message,
        data: data || null
      }
    } else if (error.request) {
      // Erro de rede (sem resposta)
      return {
        type: 'network_error',
        message: 'Erro de conexão com o servidor',
        data: null
      }
    } else {
      // Erro de configuração
      return {
        type: 'config_error',
        message: error.message || 'Erro na configuração da requisição',
        data: null
      }
    }
  }

  /**
   * Getter para acessar a instância do axios diretamente
   */
  static get axios() {
    return api
  }

  /**
   * Método para atualizar o token de autenticação
   * @param {string} token - Novo token
   */
  static setAuthToken(token) {
    if (token) {
      const bearerToken = `Bearer ${token}`
      localStorage.setItem('_token', bearerToken)
      api.defaults.headers.Authorization = bearerToken
    } else {
      localStorage.removeItem('_token')
      delete api.defaults.headers.Authorization
    }
  }

  /**
   * Método para limpar autenticação
   */
  static clearAuth() {
    localStorage.removeItem('_token')
    localStorage.removeItem('token') // Remove ambos por compatibilidade
    delete api.defaults.headers.Authorization
  }
}

export default ApiService